import { useEffect, useRef } from "react";
import moment from "moment";
import ls from "local-storage";
import swal from "sweetalert";
import AuthApi from "../Services/Authapi";
import {
  getEventId,
  getEffectiveCategory,
  getEventStartMoment,
  getEventOwnerId,
  getCleanEventTitle,
  formatMeetingTitle,
  isMeetingRecipient,
  getLeaveStatus,
  getLeaveOwnerName,
  isUserActive,
  isUserActiveFromList,
} from "../../utils/calendarReminderUtils";

const TRIGGERED_STORAGE_KEY = "cherrypik_calendar_reminders_triggered";
const CHECK_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes (prevents spamming calendar calls on all pages)
const MEETING_24H_WINDOW_MIN = 1380.0;
const MEETING_24H_WINDOW_MAX = 1440.5;
const MEETING_10M_WINDOW_MIN = 0.0;
const MEETING_10M_WINDOW_MAX = 10.5;

const isAuthRoute = (customPath) => {
  if (typeof window === "undefined") return true;
  const path = (customPath || window.location.pathname || "").toLowerCase();
  const normalized = path.replace(/\/+$/, "") || "/";
  return (
    normalized === "" ||
    normalized === "/" ||
    normalized.includes("/login") ||
    normalized.includes("/forgotpassword") ||
    normalized.includes("/forgot-password") ||
    normalized.includes("/resetpassword") ||
    normalized.includes("/reset-password")
  );
};

const getValidAuthToken = () => {
  try {
    const token = AuthApi.getStoredAuthToken
      ? AuthApi.getStoredAuthToken()
      : (ls("authToken") || localStorage.getItem("authToken"));
    if (
      !token ||
      token === "false" ||
      token === false ||
      token === "null" ||
      token === "undefined"
    ) {
      return null;
    }
    return token;
  } catch (e) {
    return null;
  }
};

const getTriggeredSet = () => {
  try {
    const raw = localStorage.getItem(TRIGGERED_STORAGE_KEY);
    if (raw) {
      const arr = JSON.parse(raw);
      if (Array.isArray(arr)) return new Set(arr);
    }
  } catch (error) {
    // ignore
  }
  return new Set();
};

const saveTriggeredSet = (setObj) => {
  try {
    const arr = Array.from(setObj);
    localStorage.setItem(TRIGGERED_STORAGE_KEY, JSON.stringify(arr.slice(-500)));
  } catch (error) {
    // ignore
  }
};

const playChimeSound = () => {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (AudioContext) {
      const ctx = new AudioContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(587.33, ctx.currentTime);
      osc.frequency.setValueAtTime(880, ctx.currentTime + 0.15);
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.6);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.6);
    }
  } catch (error) {
    // ignore
  }
};

const requestDesktopNotificationPermission = async () => {
  if (!window.Notification) {
    console.warn("[Reminder] Browser Notification API not supported.");
    return "unsupported";
  }

  if (
    window.Notification.permission === "granted" ||
    window.Notification.permission === "denied"
  ) {
    console.info("[Reminder] Notification permission:", window.Notification.permission);
    return window.Notification.permission;
  }

  try {
    const permission = await window.Notification.requestPermission();
    console.info("[Reminder] Notification permission requested:", permission);
    return permission;
  } catch (error) {
    console.warn("[Reminder] Notification permission request failed:", error);
    return "default";
  }
};

const ensureReminderServiceWorkerRegistration = async () => {
  if (!("serviceWorker" in navigator)) {
    console.warn("[Reminder] Service worker not supported.");
    return null;
  }

  try {
    let registration = await navigator.serviceWorker.getRegistration("/");

    if (!registration) {
      registration = await navigator.serviceWorker.register("/reminder-sw.js", { scope: "/" });
      console.info("[Reminder] Service worker registered.");
    } else {
      console.info("[Reminder] Existing service worker found.");
    }

    if (registration.update) {
      await registration.update();
    }

    await navigator.serviceWorker.ready;

    const activeRegistration = await navigator.serviceWorker.getRegistration("/");
    console.info("[Reminder] Service worker ready:", !!activeRegistration);
    return activeRegistration || registration;
  } catch (error) {
    console.warn("[Reminder] Service worker registration error:", error);
    return null;
  }
};

const showDesktopReminderNotification = async (title, message) => {
  if (!window.Notification || window.Notification.permission !== "granted") {
    console.warn("[Reminder] Desktop notification skipped due to permission:", window.Notification?.permission);
    return false;
  }

  const iconUrl = window.location.origin + '/logo.png';
  const badgeUrl = window.location.origin + '/logo.png';
  const notificationOptions = {
    body: message,
    icon: iconUrl,
    badge: badgeUrl,
    requireInteraction: false,
    silent: false,
    renotify: true,
    tag: `cherrypik-reminder-${title}-${message}`,
    timestamp: Date.now(),
    data: { url: window.location.href || "/dashboard" },
  };

  try {
    const registration = await ensureReminderServiceWorkerRegistration();
    if (registration && registration.showNotification) {
      await registration.showNotification(title, notificationOptions);
      console.info("[Reminder] Service worker notification shown:", { title, message });
      return true;
    }
  } catch (error) {
    console.warn("[Reminder] Service worker showNotification failed:", error);
  }

  try {
    const notif = new window.Notification(title, notificationOptions);
    notif.onclick = () => {
      window.focus();
      notif.close();
    };
    setTimeout(() => {
      try {
        notif.close();
      } catch (e) {
        // ignore
      }
    }, 4000);
    console.info("[Reminder] Window notification fallback shown:", { title, message });
    return true;
  } catch (error) {
    console.warn("[Reminder] Window notification fallback failed:", error);
  }

  return false;
};

const showNotificationPopup = async (title, message) => {
  if (isAuthRoute() || !getValidAuthToken()) {
    return;
  }
  playChimeSound();

  try {
    window.dispatchEvent(
      new CustomEvent("cherrypik_new_notification", {
        detail: { title, message },
      })
    );
  } catch (error) {
    // ignore
  }

  const permission = await requestDesktopNotificationPermission();
  console.info("[Reminder] Preparing reminder notification with permission:", permission);
  if (permission === "granted") {
    await showDesktopReminderNotification(title, message);
  }

  if (document.visibilityState === "visible" && document.hasFocus() && !isAuthRoute()) {
    swal({
      title,
      text: message,
      icon: "info",
      timer: 10000,
      buttons: false,
    });
  }
};

const CalendarReminderNotifier = () => {
  const isCheckingRef = useRef(false);
  const isInitialDelayPassedRef = useRef(false);

  useEffect(() => {
    const isAuth = isAuthRoute() || !getValidAuthToken() || !ls("user");
    if (isAuth) return;

    ensureReminderServiceWorkerRegistration();

    const tryEnablePermissionOnInteraction = () => {
      if (window.Notification && window.Notification.permission === "default") {
        requestDesktopNotificationPermission();
      }
    };

    const handleServiceWorkerMessage = (event) => {
      if (event?.data?.type === "CHERRYPIK_REMINDER_NOTIFICATION_CLICK") {
        window.focus();
      }
    };

    document.addEventListener("click", tryEnablePermissionOnInteraction);
    document.addEventListener("keydown", tryEnablePermissionOnInteraction);

    if (navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener("message", handleServiceWorkerMessage);
    }

    const checkReminders = async () => {
      if (isCheckingRef.current) return;
      const user = ls("user");
      const authToken = getValidAuthToken();
      if (!user || !authToken) return;

      const currentPath = window.location.pathname;
      const isDashboardRoute = currentPath === "/" || currentPath === "/dashboard" || currentPath === "/home";
      if (!isDashboardRoute) return;

      isCheckingRef.current = true;
      try {
        const roles = ls("roles");
        const currentUserId = String(user.id || user.Id || "");
        const roleList = Array.isArray(roles) ? roles : [String(roles || "")];
        const isAdmin = roleList.some((role) => {
          const normalizedRole = String(role).toLowerCase();
          return normalizedRole.includes("super admin") || normalizedRole.includes("admin");
        });

        const now = moment();
        const todayStr = now.format("YYYY-MM-DD");
        const tomorrowStr = now.clone().add(1, "day").format("YYYY-MM-DD");
        const in2DaysStr = now.clone().add(2, "days").format("YYYY-MM-DD");

        const response = await AuthApi.getDashboardCalendar({
          start: now.clone().subtract(1, "day").format("YYYY-MM-DD"),
          end: now.clone().add(3, "days").format("YYYY-MM-DD"),
        });

        if (!response || response.status !== true || !response.data) {
          return;
        }

        const events = Array.isArray(response.data.events) ? response.data.events : [];
        const triggered = getTriggeredSet();
        let updated = false;

        events.forEach((event) => {
          const effectiveCategory = getEffectiveCategory(event);
          const startMoment = getEventStartMoment(event, effectiveCategory !== "meeting");
          if (!startMoment.isValid()) return;

          const eventId = String(getEventId(event) ?? getCleanEventTitle(event));
          const eventDateStr = startMoment.format("YYYY-MM-DD");
          const title = getCleanEventTitle(event);

          if (effectiveCategory === "birthday") {
            const ownerId = getEventOwnerId(event) || event.user_id || event.userId;
            if (event.user && !isUserActive(event.user)) return;
            if (startMoment.format("MM-DD") === now.format("MM-DD")) {
              const key = `birthday_${todayStr}_${eventId}`;
              if (!triggered.has(key)) {
                triggered.add(key);
                updated = true;
                showNotificationPopup(
                  "Birthday Reminder",
                  `Today is ${title}'s birthday. Wish them a happy birthday!`
                );
              }
            }
            return;
          }

          if (effectiveCategory === "holiday") {
            if (eventDateStr === in2DaysStr) {
              const key = `holiday_2d_${todayStr}_${eventId}`;
              if (!triggered.has(key)) {
                triggered.add(key);
                updated = true;
                showNotificationPopup(
                  "Holiday Reminder",
                  `Upcoming holiday "${title}" is in 2 days on ${startMoment.format("ddd, MMM D")}.`
                );
              }
            } else if (eventDateStr === tomorrowStr) {
              const key = `holiday_1d_${todayStr}_${eventId}`;
              if (!triggered.has(key)) {
                triggered.add(key);
                updated = true;
                showNotificationPopup(
                  "Holiday Reminder",
                  `Upcoming holiday "${title}" is tomorrow on ${startMoment.format("ddd, MMM D")}.`
                );
              }
            }
            return;
          }

          if (effectiveCategory === "leave") {
            const leaveStatus = getLeaveStatus(event);

            if (leaveStatus !== "approved" && leaveStatus !== "pending") {
              return;
            }

            if (leaveStatus === "pending" && !isAdmin) {
              return;
            }

            const ownerId = getEventOwnerId(event);
            const isMyLeave =
              ownerId !== null && currentUserId !== null && String(ownerId) === String(currentUserId);
            const isLeaveRecipient = isAdmin || isMyLeave;

            if (isLeaveRecipient && eventDateStr === tomorrowStr) {
              const key = `leave_1d_${leaveStatus}_${todayStr}_${eventId}_${currentUserId}`;
              if (!triggered.has(key)) {
                triggered.add(key);
                updated = true;

                const cleanTitle = (title || "Leave")
                  .replace(/^(leave|reminder)\s*[-:]\s*/i, "")
                  .replace(/\bPending\s+Leave\b/i, "Leave")
                  .replace(/\bApproved\s+Leave\b/i, "Leave")
                  .trim() || "Leave";

                const notifTitle = leaveStatus === "pending" ? "Pending Leave Reminder" : "Leave Reminder";
                const notifMsg = leaveStatus === "pending"
                  ? `Pending leave reminder: ${cleanTitle} starts tomorrow on ${startMoment.format("ddd, MMM D")}.`
                  : `Leave reminder: ${cleanTitle} starts tomorrow on ${startMoment.format("ddd, MMM D")}.`;

                showNotificationPopup(notifTitle, notifMsg);
              }
            }
            return;
          }

          if (
            effectiveCategory === "time-entry" ||
            effectiveCategory === "time_entry" ||
            effectiveCategory === "time-entry-pending"
          ) {
            // Do not show swal popup dialog for time entries on admin or user side
            return;
          }

          if (effectiveCategory !== "meeting") {
            return;
          }

          if (!isMeetingRecipient(event, { isAdmin, currentUserId })) {
            return;
          }

          const minutesUntil = startMoment.diff(now, "minutes", true);
          const meetingTitle = formatMeetingTitle(event);
          const startTimeDisplay = startMoment.format("ddd, MMM D [at] h:mm A");

          if (minutesUntil >= MEETING_24H_WINDOW_MIN && minutesUntil <= MEETING_24H_WINDOW_MAX) {
            const key = `meeting_24h_${eventId}_${startMoment.format("YYYY-MM-DD_HH:mm")}_${currentUserId}`;
            if (!triggered.has(key)) {
              triggered.add(key);
              updated = true;
              showNotificationPopup(
                "Meeting Reminder",
                `Reminder: "${meetingTitle}" starts in 24 hours on ${startTimeDisplay}.`
              );
            }
          }

          if (minutesUntil >= MEETING_10M_WINDOW_MIN && minutesUntil <= MEETING_10M_WINDOW_MAX) {
            const key = `meeting_10m_${eventId}_${startMoment.format("YYYY-MM-DD_HH:mm")}_${currentUserId}`;
            if (!triggered.has(key)) {
              triggered.add(key);
              updated = true;
              showNotificationPopup(
                "Meeting Starting Soon",
                `Reminder: "${meetingTitle}" starts in 10 minutes at ${startMoment.format("h:mm A")}.`
              );
            }
          }
        });

        if (updated) {
          saveTriggeredSet(triggered);
        }
      } catch (error) {
        console.error("Error in CalendarReminderNotifier:", error);
      } finally {
        isCheckingRef.current = false;
      }
    };

    let intervalId = null;
    isInitialDelayPassedRef.current = false;

    // Wait 30 seconds after login / mount before running reminder checks
    const initialDelayTimer = setTimeout(() => {
      isInitialDelayPassedRef.current = true;
      if (!isAuthRoute() && getValidAuthToken()) {
        checkReminders();
      }
      intervalId = setInterval(() => {
        if (!isAuthRoute() && getValidAuthToken()) {
          checkReminders();
        }
      }, CHECK_INTERVAL_MS);
    }, 30000);

    const handleVisibilityChange = () => {
      if (typeof document !== "undefined" && !document.hidden && isInitialDelayPassedRef.current) {
        if (!isAuthRoute() && getValidAuthToken()) {
          checkReminders();
        }
      }
    };

    const handleRefresh = () => {
      if (!isAuthRoute() && getValidAuthToken()) {
        checkReminders();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("cherrypik_refresh_notifications", handleRefresh);

    return () => {
      clearTimeout(initialDelayTimer);
      if (intervalId) clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("cherrypik_refresh_notifications", handleRefresh);
      document.removeEventListener("click", tryEnablePermissionOnInteraction);
      document.removeEventListener("keydown", tryEnablePermissionOnInteraction);
      if (navigator.serviceWorker) {
        navigator.serviceWorker.removeEventListener("message", handleServiceWorkerMessage);
      }
    };
  }, []);

  return null;
};

export default CalendarReminderNotifier;





